import React, { useState } from 'react';
import SEO from '../components/SEO';
import { trackFilterChange } from '../components/Analytics';
import caseStudy1 from '../assets/images/case-study-1-analytics.jpg';
import caseStudy2 from '../assets/images/case-study-2-ai-robot.jpg';
import caseStudy3 from '../assets/images/case-study-3-dashboard.jpg';
import caseStudy4 from '../assets/images/case-study-4-meeting.jpg';
import caseStudy5 from '../assets/images/case-study-5-team-discussion.jpg';
import caseStudy6 from '../assets/images/case-study-6-globe.jpg';
import caseStudiesSuccess from '../assets/images/case-studies-success.jpg';

function CaseStudies() {
  const [activeFilter, setActiveFilter] = useState('All');
  
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    trackFilterChange('case_studies', filter);
  };

  const caseStudies = [
    {
      id: 1,
      title: 'Fortune 500 Digital Transformation',
      industry: 'Financial Services',
      category: 'Financial Services',
      icon: '🏦',
      description: 'Led a comprehensive digital transformation initiative for a Fortune 500 financial institution, implementing AI-powered automation that reduced processing time by 60% and improved customer satisfaction by 45%.',
      image: caseStudy1
    },
    {
      id: 2,
      title: 'Intelligent Process Automation',
      industry: 'Manufacturing',
      category: 'AI',
      icon: '🏭',
      description: 'Deployed agentic AI solutions across manufacturing operations, achieving 40% reduction in operational costs and 35% improvement in production efficiency through predictive maintenance.',
      image: caseStudy2
    },
    {
      id: 3,
      title: 'AI-Powered Customer Experience',
      industry: 'Retail & E-commerce',
      category: 'AI',
      icon: '🛒',
      description: 'Implemented AI-driven personalization and recommendation engines that increased conversion rates by 55% and customer lifetime value by 40% for a leading e-commerce platform.',
      image: caseStudy3
    },
    {
      id: 4,
      title: 'Data Analytics Platform',
      industry: 'Healthcare',
      category: 'Financial Services',
      icon: '🏥',
      description: 'Developed a comprehensive data analytics platform for a healthcare provider, enabling real-time insights and predictive analytics that improved patient outcomes by 30%.',
      image: caseStudy4
    },
    {
      id: 5,
      title: 'Low-Code Platform Migration',
      industry: 'Technology',
      category: 'AI',
      icon: '💻',
      description: 'Migrated legacy systems to modern low-code platforms, reducing development time by 70% and enabling business users to create applications 5x faster.',
      image: caseStudy5
    },
    {
      id: 6,
      title: 'Cloud-Native Modernization',
      industry: 'Telecommunications',
      category: 'Financial Services',
      icon: '☁️',
      description: 'Transformed on-premise infrastructure to cloud-native architecture, achieving 50% cost reduction, 99.99% uptime, and enabling global scalability.',
      image: caseStudy6
    }
  ];

  const filteredCaseStudies = activeFilter === 'All' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter);

  return (
    <div>
      <SEO 
        title="Case Studies - Real AI Transformation Success Stories"
        description="Explore Vithminds' successful AI and digital transformation projects for Fortune 500 companies. 100% project success rate, 100% client satisfaction."
        keywords="AI case studies, digital transformation success stories, enterprise AI projects, Fortune 500 transformations"
      />
      
      {/* Hero Section */}
      <section className="casestudy-hero">
        <div className="casestudy-hero-content">
          <h1>Industry Success Stories</h1>
          <p>Real transformations, measurable results, lasting partnerships</p>
          <div className="casestudy-metrics">
            <div className="casestudy-metric">
              <div className="metric-icon">🎯</div>
              <div className="metric-value">100%</div>
              <div className="metric-label">Project Success Rate</div>
            </div>
            <div className="casestudy-metric">
              <div className="metric-icon">😊</div>
              <div className="metric-value">100%</div>
              <div className="metric-label">Client Satisfaction</div>
            </div>
            <div className="casestudy-metric">
              <div className="metric-icon">⚡</div>
              <div className="metric-value">3x</div>
              <div className="metric-label">Faster Time-to-market</div>
            </div>
          </div>
        </div>
        <div className="casestudy-hero-image">
          <img src={caseStudiesSuccess} alt="Success" />
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="section">
        <h2 className="section-title">Case Studies</h2>
        <p className="section-subtitle">
          Know how Vithminds helps its clients realize their business potential
        </p>

        {/* Filter Buttons */}
        <div className="casestudy-filters" role="group" aria-label="Filter case studies">
          <button 
            className={`filter-btn ${activeFilter === 'All' ? 'active' : ''}`}
            onClick={() => handleFilterChange('All')}
            aria-pressed={activeFilter === 'All'}
            aria-label="Show all case studies"
          >
            All
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'Financial Services' ? 'active' : ''}`}
            onClick={() => handleFilterChange('Financial Services')}
            aria-pressed={activeFilter === 'Financial Services'}
            aria-label="Filter by Financial Services"
          >
            Financial Services
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'AI' ? 'active' : ''}`}
            onClick={() => handleFilterChange('AI')}
            aria-pressed={activeFilter === 'AI'}
            aria-label="Filter by AI"
          >
            AI
          </button>
        </div>

        {/* Case Studies Grid */}
        <div className="casestudy-grid">
          {filteredCaseStudies.map(study => (
            <div key={study.id} className="casestudy-card-new">
              <span className="casestudy-tag">{study.industry}</span>
              <div className="casestudy-image-wrapper">
                <img src={study.image} alt={study.title} className="casestudy-image" />
              </div>
              <div className="casestudy-content">
                <h3 className="casestudy-title">{study.title}</h3>
                <p className="casestudy-description">{study.description}</p>
                <a href={`/case-studies/${study.id}`} className="casestudy-link">Read more</a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="casestudy-cta-section">
        <div className="casestudy-cta-content">
          <h2>Build AI that works for you</h2>
          <p>Talk to us today about crafting a custom AI solution tailored to your business.</p>
          <a href="/contact-us" className="casestudy-cta-button">Book a free Consultation</a>
        </div>
      </section>
    </div>
  );
}

export default CaseStudies;
